import { useState, type FormEvent } from 'react';
import { createWebApiClient } from '../../api/webSession.js';
import { navigateHashPath, safeReturnPath, storePostOnboardingReturnTo } from '../../app/return-to.js';
import { hrefForRoute, navigateTo } from '../../app/router.js';
import { Button, Field, IconGlyph } from '../../ui/primitives/index.js';

export function AuthScreen(props: { mode: 'login' | 'register'; returnTo?: string | null }) {
  const [status, setStatus] = useState<'ready' | 'saving' | 'failed'>('ready');
  const [error, setError] = useState('');
  const isRegister = props.mode === 'register';
  const returnNotice = Boolean(props.returnTo);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const username = String(form.get('username') || '');
    const password = String(form.get('password') || '');
    const confirmPassword = String(form.get('confirmPassword') || '');
    if (isRegister && password !== confirmPassword) {
      setStatus('failed');
      setError('The two passwords do not match.');
      return;
    }
    setStatus('saving');
    setError('');
    try {
      const api = createWebApiClient();
      if (isRegister) await api.register(username, password);
      else await api.login(username, password);
      const returnTo = safeReturnPath(props.returnTo);
      if (isRegister) {
        storePostOnboardingReturnTo(returnTo);
        navigateTo('onboarding-profile');
      } else if (returnTo) {
        navigateHashPath(returnTo);
      } else {
        navigateTo('today');
      }
    } catch (err) {
      setStatus('failed');
      setError(isRegister ? 'The account could not be created. Check your entries and try again.' : 'Incorrect username or password. Please try again.');
    }
  }

  return (
    <article className={`mira-auth-screen mira-botanical-page ${isRegister ? 'mira-auth-screen-register' : 'mira-auth-screen-login'}`}>
      <div className="mira-leaf-wash" aria-hidden="true" />
      <header className="mira-auth-hero">
        <div className="mira-auth-logo-mark" aria-label="Mira">
          <span aria-hidden="true" />
          <strong>Mira</strong>
          <em>{isRegister ? 'Start knowing yourself here' : 'See within, and see your relationships clearly'}</em>
        </div>
      </header>

      <form className="mira-auth-form mira-auth-card" onSubmit={submit}>
        {returnNotice ? (
          <section className="mira-auth-alert mira-auth-notice">
            <span aria-hidden="true"><IconGlyph value="↩" /></span>
            <div>
              <strong>You'll return where you left off</strong>
              <p>After signing in and completing the essentials, Mira brings you back to what you opened.</p>
            </div>
          </section>
        ) : null}
        {error ? (
          <section className="mira-auth-alert" role="alert">
            <span aria-hidden="true"><IconGlyph value="!" /></span>
            <div>
              <strong>{isRegister ? 'Could not create the account' : 'Could not sign in'}</strong>
              <p>{error}</p>
            </div>
          </section>
        ) : null}
        <Field className="mira-auth-icon-field" label="Username" name="username" placeholder="Enter username" autoComplete="username" required />
        <Field className="mira-auth-icon-field" label="Password" name="password" placeholder="Enter password" autoComplete={isRegister ? 'new-password' : 'current-password'} type="password" required minLength={8} hint={isRegister ? 'At least 8 characters.' : undefined} />
        {isRegister ? <Field className="mira-auth-icon-field" label="Confirm password" name="confirmPassword" placeholder="Re-enter password" type="password" required minLength={8} /> : null}
        <Button busy={status === 'saving'} disabled={status === 'saving'} type="submit">{status === 'saving' ? 'One moment…' : isRegister ? 'Create account' : 'Sign in'}</Button>
      </form>

      <footer className="mira-auth-footer">
        <div className="mira-divider"><span>or</span></div>
        {isRegister ? (
          <p>Already have an account? <a href={hrefForRoute('login', {}, props.returnTo ? { returnTo: props.returnTo } : {})}>Sign in</a></p>
        ) : (
          <p>No account yet? <a href={hrefForRoute('register', {}, props.returnTo ? { returnTo: props.returnTo } : {})}>Create account</a></p>
        )}
        <p className="mira-legal-copy">
          By continuing you agree to Mira's <a href={hrefForRoute('terms', {}, { source: isRegister ? 'register' : 'login' })}>Terms of Service</a> and <a href={hrefForRoute('privacy', {}, { source: isRegister ? 'register' : 'login' })}>Privacy Policy</a>.
        </p>
      </footer>
      <div className="mira-bottom-landscape" aria-hidden="true" />
    </article>
  );
}

export function BrandWord() {
  return <div className="mira-brand-word" aria-label="Mira"><span aria-hidden="true" />Mira</div>;
}
